/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import React, { useState, useEffect, memo, useCallback } from 'react';
import Rating, { AnalyticsRating } from './Rating';
import './styles.scss';
import { RatingGroupProps } from './types';
import { generateID } from '../../base/helper';

const getValue = (index: number) => {
  const value = Math.floor(index);
  if (value >= 5) return 5;
  if (value <= 0) return 0;
  return value;
};

const getValueFromCondition = (
  condition?: number,
  defaultCondition?: number
) => {
  if (condition !== undefined) return getValue(condition);
  if (defaultCondition !== undefined) return getValue(defaultCondition);
  return 0;
};

/**
 * UI Toolkit Rating Group component
 * @public
 */
const RatingGroup: React.FC<RatingGroupProps> = ({
  id = generateID('clientos-ui-toolkit-rating-group'),
  condition,
  onChange,
  defaultCondition,
  ariaLabels = ['1 star', '2 stars', '3 stars', '4 stars', '5 stars'],
  trackId,
  canReturnParams,
  stopTracking,
  trackCategory,
  trackedHandlers,
  untrackedHandlers
}) => {
  const [value, setValue] = useState(0);
  const [currentValue, setCurrentValue] = useState(0);
  const [hover, setHover] = useState<number | null>(null);

  const onMouseOver = useCallback(
    (index: number) => {
      if (index !== currentValue) setCurrentValue(index);
      if (hover !== index) setHover(index);
    },
    [currentValue, hover]
  );

  const onMouseLeave = useCallback(() => {
    if (condition === undefined) {
      if (value !== currentValue) setCurrentValue(value);
    } else {
      const newValue = getValue(condition);
      if (newValue !== value) setValue(newValue);
      if (newValue !== currentValue) setCurrentValue(newValue);
    }
    if (hover !== null) setHover(null);
  }, [value, currentValue, hover, condition]);

  const onClick = useCallback(
    (index: number) => {
      let newValue = getValue(index);
      if (newValue === value) newValue = 0;
      if (hover !== null) setHover(null);
      if (newValue !== value) setValue(newValue);
      if (newValue !== currentValue) setCurrentValue(newValue);
      if (onChange) onChange(newValue);
    },
    [onChange, value, currentValue, hover]
  );

  const getState = useCallback(
    (index: number) => {
      if (hover !== null) {
        if (value === 0 && index <= currentValue) return 'hover';

        if (value !== 0 && index <= currentValue) return 'hover--selected';

        return 'active';
      }
      if (index <= value - 1) return 'selected';

      return 'active';
    },
    [value, hover, currentValue]
  );

  useEffect(() => {
    const newValue = getValueFromCondition(condition, defaultCondition);
    if (newValue !== value) setValue(newValue);
    if (newValue !== currentValue) setCurrentValue(newValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition, defaultCondition]);

  return (
    <div
      id={id}
      className="clientos-ui-toolkit-rating-group"
      onMouseLeave={onMouseLeave}
    >
      {Array.from({ length: 5 }).map((_, index) => {
        const aria = ariaLabels?.[index];
        return trackId ? (
          <AnalyticsRating
            id={`${id}__rating-${index + 1}`}
            trackId={trackId}
            trackCategory={trackCategory}
            trackedHandlers={trackedHandlers}
            untrackedHandlers={untrackedHandlers}
            stopTracking={stopTracking}
            canReturnParams={canReturnParams}
            key={index + 1}
            state={getState(index)}
            onMouseOver={() => onMouseOver(index)}
            onClick={() => onClick(index + 1)}
            aria-label={aria}
          />
        ) : (
          <Rating
            id={`${id}__rating-${index + 1}`}
            key={index + 1}
            state={getState(index)}
            onMouseOver={() => onMouseOver(index)}
            onClick={() => onClick(index + 1)}
            aria-label={aria}
          />
        );
      })}
    </div>
  );
};

export default memo(RatingGroup);
